import { map } from 'lodash-es';

export default {

  /**
   * @param state
   * @returns {Array}
   */
  getSeasonsWithRelations(state) {
    return map(state.seasons.items, u => ({
      id: u.id,
      twentySupplierId: u.twentySupplierId,
      name: u.name,
      arrivalFrom: u.arrivalFrom,
      arrivalTo: u.arrivalTo,
      webActive: u.webActive,
      active: u.active,
      bookDaysBeforeArrival: u.bookDaysBeforeArrival,
      netOrGross: u.netOrGross,
      priceSettings: u.priceSettings,
      numPersonCategories: Object.keys(u.personCategories).length,
      numPeriods: Object.keys(u.periodsPrices).length,
      numAreas: Object.keys(u.seasonAreas).length,
      numDiscounts: Object.keys(u.discounts).length,
    }));
  },

  /**
   * @param state
   * @returns {{
   * numAreas: *, arrivalTo: *, webActive: *, active: *, twentySupplierId: *, arrivalFrom: *,
   * netOrGross: *, priceSettings: *, numDiscounts: *, numPeriods: *, numPersonCategories: *,
   * name: *, id: *
   * }}
   */
  getSeasonWithRelations(state) {
    const season = state.season;
    return {
      id: season.id,
      twentySupplierId: season.twentySupplierId,
      name: season.name,
      arrivalFrom: season.arrivalFrom,
      arrivalTo: season.arrivalTo,
      webActive: season.webActive,
      active: season.active,
      bookDaysBeforeArrival: season.bookDaysBeforeArrival,
      netOrGross: season.netOrGross,
      priceSettings: season.priceSettings,
      numPersonCategories: Object.keys(season.personCategories.items).length,
      numPeriods: Object.keys(season.pricePeriods.items).length,
      numAreas: Object.keys(season.skiAreas.ids).length,
      numDiscounts: Object.keys(season.discounts.items).length,
    };
  },

  /**
   * @param state
   * @returns {Array}
   */
  getPersonCategories(state) {
    let personCategories = state.season.personCategories.items;
    if (!personCategories || !Object.keys(personCategories).length) {
      return [];
    }
    personCategories = map(state.season.personCategories.items, personCategory => ({
      id: personCategory.id,
      skiPassSeasonId: personCategory.skiPassSeasonId,
      name: personCategory.name,
      externalName: personCategory.externalName,
      ageFrom: personCategory.ageFrom,
      ageTo: personCategory.ageTo,
      xmlChannelProductIds: personCategory.xmlChannelProductIds,
      domains: personCategory.domains || {},
    }));
    return personCategories.sort((a, b) => a.ageFrom - b.ageFrom);
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getDiscounts(state) {
    let discounts = state.season.discounts.items;
    if (!discounts || !Object.keys(discounts).length) {
      return [];
    }

    discounts = map(state.season.discounts.items, discount => ({
      id: discount.id,
      skiPassSeasonId: discount.skiPassSeasonId,
      bookFrom: discount.bookFrom,
      bookTo: discount.bookTo,
      arrivalFrom: discount.arrivalFrom,
      arrivalTo: discount.arrivalTo,
      minPax: discount.minPax,
      maxPax: discount.maxPax,
      percentage: discount.percentage,
      personCategories: discount.personCategories,
    }));
    return discounts.sort((a, b) => a.bookFrom - b.bookFrom);
  },

  /**
   * @param state
   * @param getters
   * @returns {*}
   */
  getActivePricePeriods(state, getters) {
    const periodPrices = getters.groupPeriodPrices(state);
    const todayDate = getters.getTodayDate;
    const activePricePeriods = periodPrices.filter(period =>
      period.arrivalTo >= todayDate || period.arrivalTo === undefined, ).sort((a, b) => {
        if (!b.arrivalTo) {
          return 0;
        }
        return b.arrivalTo.localeCompare(a.arrivalTo);
    });
    return activePricePeriods.map(period => {
      const days = {};
      Object.entries(period.days).forEach(([ key, value ]) => {
        days[key] = {
          ...value,
          toggled: false,
        };
      });
      return {
        ...period,
        days,
      };
    });
  },

  /**
   * @param state
   * @param getters
   * @returns {*}
   */
  getHistoricallyPricePeriods(state, getters) {
    const periodPrices = getters.groupPeriodPrices(state);
    const todayDate = getters.getTodayDate;
    return periodPrices.filter(period =>
      period.arrivalTo < todayDate, ).sort((a, b) =>
        b.arrivalFrom.localeCompare(a.arrivalFrom), );
  },

  /**
   * @returns {Function}
   */
  groupPeriodPrices: () => state => {
    const statePeriods = Object.values(state.season.pricePeriods.items);
    const personCategories = Object.values(state.season.personCategories.items);
    if (!personCategories || personCategories.length === 0) {
      return [];
    }
    const periods = statePeriods.sort((a, b) => {
      if (!b.arrivalTo) {
        return 0;
      }
      return b.arrivalTo.localeCompare(a.arrivalTo);
    });
    const periodsGrouped = {};
    const periodPricesGrouped = {};
    const dates = {};
    for (let i = 0; i < periods.length; i += 1) {
      const period = periods[i];
      const days = {};
      if (!period.prices) {
        period.prices = {};
      }
      let periodPairedWithPeriodId;
      const prices = Object.values(period.prices);
      for (let j = 0; j < prices.length; j += 1) {
        const priceDay = prices[j];
        if (periodsGrouped[priceDay.id] !== undefined) {
          periodPairedWithPeriodId = periodsGrouped[priceDay.id];
          break;
        }
        periodsGrouped[priceDay.id] = period.id;
        if (!days[priceDay.days]) {
          days[priceDay.days] = {
            personCategories: {},
          };
          for (let k = 0; k < personCategories.length; k += 1) {
            const personCategory = personCategories[k];
            days[priceDay.days].personCategories[personCategory.id] = {
              id: undefined,
              skiPassPersonCategoryId: personCategory.id,
              days: priceDay.days,
              price: undefined,
              name: personCategory.name,
              externalName: personCategory.externalName,
              ageFrom: personCategory.ageFrom,
              ageTo: personCategory.ageTo,
              commissionRatio: undefined,
              contributionRatio: undefined,
              requireKeyCard: priceDay.requireKeyCard,
            };
          }
        }
        const currentPersonCategory =
          days[priceDay.days].personCategories[priceDay.skiPassPersonCategoryId];
        if (currentPersonCategory) {
          currentPersonCategory.id = priceDay.id;
          currentPersonCategory.price = priceDay.price;
          currentPersonCategory.commissionRatio = priceDay.commissionRatio;
          currentPersonCategory.contributionRatio = priceDay.contributionRatio;
          currentPersonCategory.requireKeyCard = priceDay.requireKeyCard;
        }
      }
      const targetPeriodId = periodPairedWithPeriodId || period.id;
      if (dates[targetPeriodId] === undefined) {
        dates[targetPeriodId] = {};
      }
      dates[targetPeriodId][period.id] = {
        id: period.id,
        skiPassSeasonId: period.skiPassSeasonId,
        arrivalFrom: period.arrivalFrom,
        arrivalTo: period.arrivalTo,
      };
      const sortedDates = Object.values(dates[targetPeriodId]).sort((a, b) => {
        if (!b.arrivalTo) {
          return 0;
        }
        return b.arrivalTo.localeCompare(a.arrivalTo);
      });
      if (periodPairedWithPeriodId) {
        periodPricesGrouped[periodPairedWithPeriodId] = {
          ...periodPricesGrouped[periodPairedWithPeriodId],
          dates: sortedDates,
        };
      } else {
        periodPricesGrouped[period.id] = {
          id: period.id,
          periodPairedWithPeriodId,
          skiPassSeasonId: period.skiPassSeasonId,
          arrivalFrom: period.arrivalFrom,
          arrivalTo: period.arrivalTo,
          days,
          dates: sortedDates,
        };
      }
    }

    return Object.values(periodPricesGrouped);
  },

  /**
   * @returns {string}
   */
  getTodayDate: () => new Date().toISOString().substr(0, 10),

  /**
   * @param state
   * @returns {this|Array}
   */
  getSkiSuppliers(state) {
    const stateSuppliers = state.suppliers.items;
    if (!stateSuppliers || !Object.keys(stateSuppliers).length) {
      return [];
    }
    const suppliers = map(stateSuppliers, supplier => ({
      id: supplier.id,
      name: supplier.name,
    }));
    return suppliers.sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getSalesTypes(state) {
    const stateSalesTypes = state.salesTypes.items;
    if (!stateSalesTypes || !Object.keys(stateSalesTypes).length) {
      return [];
    }
    const salesTypes = map(stateSalesTypes, salesTypeIdentifier => {
      let salesTypeName = '';
      switch (salesTypeIdentifier) {
        case 'booking':
          salesTypeName = 'Pay now (default)';
          break;
        case 'bookingArrival':
          salesTypeName = 'Book & pay upon arrival';
          break;
        case 'arrival':
          salesTypeName = 'Pay upon arrival';
          break;
        default:
          salesTypeName = salesTypeIdentifier;
          break;
      }
      return {
        id: salesTypeIdentifier,
        name: salesTypeName,
      };
    });

    return salesTypes.sort((a, b) => a.name.localeCompare(b.name));
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/ski/skipass/getters.js